import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AlertTriangle, Mail, Wrench, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAINTENANCE_BYPASS_PATHS = ['/login', '/login/admin', '/payment/success', '/payment/cancel'];

const MaintenanceGuard = ({ children }) => {
  const { user } = useAuth();
  const { language, localize } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [maintenance, setMaintenance] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkMaintenance();
    const interval = setInterval(checkMaintenance, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkMaintenance = async () => {
    try {
      const res = await axios.get(`${API}/maintenance-status`);
      setMaintenance(res.data.enabled === true);
    } catch {
      setMaintenance(false);
    } finally {
      setLoading(false);
    }
  };

  // Admin can always bypass
  if (user?.role === 'admin') return children;

  // Allow login pages during maintenance so admin can log in
  if (MAINTENANCE_BYPASS_PATHS.some(p => location.pathname.startsWith(p))) return children;

  if (loading) return null;

  if (!maintenance) return children;

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="maintenance-page">
      <div className="max-w-lg w-full text-center space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <img loading="lazy" src="/lwt-logo-new.png" alt="Learn With Trust" className="w-24 h-24 rounded-2xl" />
        </div>

        {/* Animated Icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center animate-pulse">
            <Wrench className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        {/* Title */}
        <div>
          <h1 className="text-3xl font-bold text-white mb-3">
            {localize({ ar: 'الموقع تحت الصيانة', en: 'Site Under Maintenance' })}
          </h1>
          <div className="flex items-center justify-center gap-2 text-orange-400 mb-4">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm font-medium">
              {localize({ ar: 'نأسف على الإزعاج', en: 'We apologize for the inconvenience' })}
            </span>
          </div>
          <p className="text-slate-400 text-lg leading-relaxed">
            {localize({ ar: 'نعمل حالياً على تحسين المنصة. سنعود قريباً بإذن الله.', en: 'We are currently improving the platform. We will be back soon.' })}
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-700" />

        {/* Contact */}
        <div className="space-y-3">
          <p className="text-slate-500 text-sm">
            {localize({ ar: 'يمكنكم التواصل عبر الإيميل', en: 'You can contact us via email' })}
          </p>
          <a
            href="mailto:info@trustedtechno.com"
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-emerald-400 px-6 py-3 rounded-xl text-lg font-semibold transition-colors"
            data-testid="maintenance-email-link"
          >
            <Mail className="w-5 h-5" />
            info@trustedtechno.com
          </a>
        </div>

        {/* Footer */}
        <p className="text-slate-600 text-xs">
          Learn With Trust &copy; {new Date().getFullYear()}
        </p>

        {/* Admin Access */}
        <Button
          variant="ghost"
          onClick={() => navigate('/login')}
          className="text-slate-600 hover:text-slate-300 text-xs gap-1.5"
          data-testid="maintenance-admin-login-btn"
        >
          <Lock className="w-3 h-3" />
          {localize({ ar: 'دخول المسؤول', en: 'Admin Login' })}
        </Button>
      </div>
    </div>
  );
};

export default MaintenanceGuard;
